// Firmware deployment panel (#1049) — 3CX-style push dialog launched from the
// Phones toolbar. Pick a firmware from the catalog (/api/firmwares), target the
// whole fleet matching the firmware's vendor+model or the phones selected in
// the list, push, then watch per-device status (pending -> updated on the next
// provision cycle). Push/status/cancel wire to /api/firmwares/deploy*.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircle, Star, X } from 'lucide-react';
import { Dialog } from './Dialog';
import { useToast } from './Toast';
import { getToken } from '../lib/api';
import { bearerHeaders } from '../lib/scopedDomain';

type Firmware = {
  firmware_uuid: string; vendor: string; model: string; version: string;
  is_recommended?: boolean; notes?: string | null;
};
type DeployRow = {
  device_mac: string; extension?: string | null; device_model?: string | null;
  device_label?: string | null; current_firmware?: string | null;
  target_vendor?: string | null; target_model?: string | null; target_version?: string | null;
  status: 'pending' | 'updated';
};

const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...bearerHeaders(getToken(), true), ...(init.headers || {}) } });
  if (!r.ok) throw new Error((await r.text()) || r.statusText);
  return r.json();
};

const sameModel = (p: any, fw: Firmware) =>
  String(p.device_vendor || '').trim().toLowerCase() === String(fw.vendor || '').trim().toLowerCase() &&
  String(p.device_model || '').trim().toLowerCase() === String(fw.model || '').trim().toLowerCase();

export default function FirmwareDeployModal({ open, onClose, tid, phones, picked }: {
  open: boolean; onClose: () => void;
  tid?: string;                // scoped department (getScopedDomain from the page)
  phones: any[];               // provisioned phones already loaded by the page
  picked: string[];            // device_uuids checked in the Phones list
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [fwUuid, setFwUuid] = useState('');
  const [mode, setMode] = useState<'fleet' | 'selected'>(picked.length ? 'selected' : 'fleet');

  const { data: firmwares = [] } = useQuery<Firmware[]>({
    queryKey: ['firmwares-catalog'],
    queryFn: () => fJson('/api/firmwares'),
    enabled: open,
  });

  const { data: deployments = [] } = useQuery<DeployRow[]>({
    queryKey: ['firmware-deployments', tid],
    queryFn: () => fJson(`/api/firmwares/deploy/status?domain_uuid=${encodeURIComponent(tid!)}`),
    enabled: open && !!tid,
    refetchInterval: 10_000,
  });

  const fw = firmwares.find(f => f.firmware_uuid === fwUuid);
  const fleetTargets = useMemo(() => (fw ? phones.filter(p => sameModel(p, fw)) : []), [phones, fw]);
  const targetCount = mode === 'fleet' ? fleetTargets.length : picked.length;
  // Warn when a checked phone isn't the model this firmware is built for.
  const mismatched = useMemo(
    () => (fw && mode === 'selected'
      ? phones.filter(p => picked.includes(p.device_uuid) && !sameModel(p, fw)).length : 0),
    [phones, picked, fw, mode]);

  const deploy = useMutation({
    mutationFn: () => fJson('/api/firmwares/deploy', {
      method: 'POST',
      body: JSON.stringify({
        firmware_uuid: fwUuid,
        domain_uuid: tid,
        ...(mode === 'fleet'
          ? { vendor: fw?.vendor, model: fw?.model }
          : { device_uuids: picked }),
      }),
    }),
    onSuccess: (r: any) => {
      const n = (r.results || []).filter((x: any) => x.status === 'scheduled').length;
      const skipped = (r.skipped || []).length;
      toast('success', `Firmware ${r.firmware?.version || ''} queued for ${n} phone(s)${skipped ? `, ${skipped} skipped (no MAC)` : ''}. Phones update on their next provision.`);
      qc.invalidateQueries({ queryKey: ['firmware-deployments', tid] });
    },
    onError: (e: any) => toast('error', `Push failed: ${e.message}`),
  });

  const cancel = useMutation({
    mutationFn: (mac: string) => fJson(`/api/firmwares/deploy/${mac}?domain_uuid=${encodeURIComponent(tid!)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['firmware-deployments', tid] }),
    onError: (e: any) => toast('error', `Cancel failed: ${e.message}`),
  });

  return (
    <Dialog open={open} onClose={onClose} title="Deploy Firmware" size="lg"
      footer={<>
        <button className="btn-ghost" onClick={onClose}>Close</button>
        <button className="btn-primary inline-flex items-center gap-1 disabled:opacity-40"
                disabled={!fw || targetCount === 0 || !tid || deploy.isPending}
                onClick={() => deploy.mutate()}>
          <ArrowUpCircle size={14} />
          {deploy.isPending ? 'Pushing…' : `Push to ${targetCount} phone${targetCount === 1 ? '' : 's'}`}
        </button>
      </>}>
      <div className="space-y-4">
        {/* 1 — firmware */}
        <div>
          <label className="text-sm font-medium block mb-1">Firmware</label>
          <select className="input w-full" value={fwUuid} onChange={e => setFwUuid(e.target.value)}>
            <option value="">Select firmware…</option>
            {firmwares.map(f => (
              <option key={f.firmware_uuid} value={f.firmware_uuid}>
                {f.vendor} {f.model} — {f.version}{f.is_recommended ? ' (recommended)' : ''}
              </option>
            ))}
          </select>
          {firmwares.length === 0 && (
            <div className="text-xs text-textSecondary mt-1">
              No firmware files in the catalog yet — upload one on the Firmwares page first.
            </div>
          )}
          {fw?.is_recommended && (
            <div className="text-xs text-success mt-1 inline-flex items-center gap-1"><Star size={12} /> Recommended version</div>
          )}
          {fw?.notes && <div className="text-xs text-textSecondary mt-1">{fw.notes}</div>}
        </div>

        {/* 2 — target */}
        <div>
          <label className="text-sm font-medium block mb-1">Target</label>
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={mode === 'fleet'} onChange={() => setMode('fleet')} />
              <span>
                All matching phones
                {fw ? <> — <strong>{fleetTargets.length}</strong> × {fw.vendor} {fw.model}</> : ' (pick a firmware first)'}
              </span>
            </label>
            <label className={`flex items-center gap-2 text-sm ${picked.length === 0 ? 'opacity-50' : ''}`}>
              <input type="radio" checked={mode === 'selected'} disabled={picked.length === 0}
                     onChange={() => setMode('selected')} />
              <span>Selected phones ({picked.length}) — tick phones in the list before opening this dialog</span>
            </label>
          </div>
          {mismatched > 0 && (
            <div className="text-xs text-danger mt-1">
              {mismatched} selected phone(s) are not {fw?.vendor} {fw?.model} — they will still be marked; the phone ignores firmware it cannot apply.
            </div>
          )}
        </div>

        {/* 3 — per-device status */}
        <div>
          <label className="text-sm font-medium block mb-1">Deployment status</label>
          <div className="card p-0 overflow-hidden">
            <table className="table">
              <thead>
                <tr><th>Ext</th><th>MAC</th><th>Model</th><th>Current</th><th>Target</th><th>Status</th><th className="w-8"></th></tr>
              </thead>
              <tbody>
                {deployments.map(d => (
                  <tr key={d.device_mac} className="hover:bg-elevated">
                    <td className="font-mono">{d.extension || '—'}</td>
                    <td className="font-mono text-xs">{d.device_mac}</td>
                    <td>{d.device_model || d.target_model || '—'}</td>
                    <td className="font-mono text-xs">{d.current_firmware || '—'}</td>
                    <td className="font-mono text-xs">{d.target_version || '—'}</td>
                    <td>
                      {d.status === 'updated'
                        ? <span className="text-success text-xs font-medium">Updated</span>
                        : <span className="text-primary text-xs font-medium">Pending next provision</span>}
                    </td>
                    <td>
                      <button className="btn-ghost p-1 text-danger" title="Cancel pending push"
                              onClick={() => cancel.mutate(d.device_mac)}>
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {deployments.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-6 text-textSecondary text-sm">
                    No pending firmware deployments for this department.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
